<template>
  <div class="max-h-[calc(70vh-120px)]">
    <AlertLayout
      title="Why Set Up your Facility Services?"
      message="Setting up your services makes it easy to manage what your facility offers and ensures everything runs smoothly for you and your clients."
      type="warning"
    />
    <div
      v-for="(service, index) in services"
      :key="index"
      class="grid grid-cols-4 gap-x-10 gap-y-6 flex px-20 mt-5"
    >
      <div>
        <label for="department" class="block text-sm font-medium text-gray-700"
          >Department<span class="text-red-500">*</span></label
        >
        <select
          id="department"
          v-model="service.service_type_id"
          @change="handleServiceTypeChange(index)"
          class="mt-1 block w-full rounded-sm border-gray-300 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50 bg-gray-50"
        >
          <option
            v-for="serviceType in serviceTypes"
            :key="serviceType.id"
            :value="serviceType"
          >
            {{ serviceType.name }}
          </option>
        </select>
        <span
          class="text-xs text-red-500"
          v-if="service.errors.service_type_id"
          >{{ service.errors.service_type_id }}</span
        >
      </div>

      <div>
        <label for="serviceType" class="block text-sm font-medium text-gray-700"
          >Service Name<span class="text-red-500">*</span></label
        >
        <input
          type="text"
          id="genericName"
          v-model="service.name"
          placeholder="Service Name e.g Urinalysis"
          class="mt-1 block w-full rounded-sm border-gray-300 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50 bg-gray-50"
        />
        <span class="text-xs text-red-500" v-if="service.errors.name">{{
          service.errors.name
        }}</span>
      </div>

      <div v-if="service.show_sample">
        <label for="sample_id" class="block text-sm font-medium text-gray-700"
          >Lab Sample<span class="text-red-500">*</span></label
        >
        <select
          id="sample_id"
          v-model="service.sample_id"
          class="mt-1 block w-full rounded-sm border-gray-300 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50 bg-gray-50"
        >
          <option v-for="sample in samples" :key="sample.id" :value="sample.id">
            {{ sample.name }}
          </option>
        </select>
        <span class="text-xs text-red-500" v-if="service.errors.sample_id">{{
          service.errors.sample_id
        }}</span>
      </div>

      <div>
        <label for="cost" class="block text-sm font-medium text-gray-700"
          >Cost<span class="text-red-500">*</span></label
        >
        <input
          type="number"
          id="cost"
          v-model="service.unit_price"
          min="0.00"
          class="mt-1 block w-full rounded-sm border-gray-300 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50 bg-gray-50"
        />
        <span class="text-xs text-red-500" v-if="service.errors.unit_price">{{
          service.errors.unit_price
        }}</span>
      </div>

      <div>
        <div v-if="index === services.length - 1">
          <svg
            @click="addServiceRow"
            class="w-6 h-6 text-gray-800 dark:text-white"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            fill="yellow"
            viewBox="0 0 24 24"
          >
            <path
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M5 12h14m-7 7V5"
            />
          </svg>
        </div>
      </div>
    </div>
    <div class="mt-4 space-x-4 px-20">
      <button
        type="button"
        data-modal-hide="modal"
        class="px-6 mb-4 py-2 bg-gray-200 text-gray-800 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50"
      >
        Cancel
      </button>
      <button
        type="button"
        @click="handleSubmit"
        class="px-6 mb-4 r-4 py-2 bg-custom-yellow text-white hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-opacity-50"
      >
        <span v-if="!showLoader">Submit</span>
        <span v-else class="absolute inset-0 flex justify-center items-center">
          <svg
            class="animate-spin h-5 w-5 text-white"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <circle
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
              fill="none"
            />
          </svg>
        </span>
      </button>
    </div>
  </div>
</template>
<script>
import apiClient from "@/services/api";
import AlertLayout from "@/components/AlertLayout.vue";
import { boolean } from "yup";

export default {
  name: "AddFacilityService",
  components: {
    AlertLayout,
  },
  data() {
    return {
      loading: true,
      error: null,
      services: [
        {
          service_type_id: "",
          name: "",
          unit_price: "",
          sample_id: "",
          show_sample: false,
          errors: {},
        },
      ],
      serviceTypes: [],
      samples: [],
      showLoader: boolean,
    };
  },
  mounted() {
    this.fetchServiceTypes();
    this.fetchLabSamples();
  },
  computed: {},
  methods: {
    addServiceRow() {
      if (!this.validateForm()) {
        return;
      }
      this.services.push({
        service_type_id: "",
        name: "",
        unit_price: "",
        sample_id: "",
        show_sample: false,
        errors: {
          service_type_id: "",
          name: "",
          unit_price: "",
        },
      });
    },
    async fetchServiceTypes() {
      try {
        this.showLoader = true;
        const response = await apiClient.getFacilityServiceTypes();
        if (response.success) {
          this.showLoader = false;
          this.serviceTypes = response.data;
        }
      } catch (error) {
        this.showLoader = false;
        this.$toast.error("Failed to unable to fetch service types");
        console.log(error);
      }
    },
    async fetchLabSamples() {
      try {
        this.showLoader = true;
        const response = await apiClient.getLabSamples();
        if (response.success) {
          this.showLoader = false;
          this.samples = response.data;
        }
      } catch (error) {
        this.showLoader = false;
        this.$toast.error("Failed to unable to fetch lab samples");
        console.log(error);
      }
    },
    validateForm() {
      let isValid = true;

      this.services.forEach((service) => {
        service.errors = {}; // Reset errors

        if (!service.service_type_id) {
          service.errors.service_type_id = "Service Type is required.";
          isValid = false;
        }
        if (!service.name) {
          service.errors.name = "Name is required.";
          isValid = false;
        }
        if (!service.unit_price || service.unit_price <= 0) {
          service.errors.unit_price = "Unit Price must be greater than 0.";
          isValid = false;
        }
      });
      return isValid;
    },
    async handleSubmit() {
      try {
        if (!this.validateForm()) {
          return;
        }

        this.showLoader = true;

        const payload = {
          facility_services: this.services,
        };
        const response = await apiClient.createFacilityService(payload);
        if (response.success) {
          this.showLoader = false;
          this.$toast.success("Facility services created successfully");
          this.$router.push("/facility-services");
        } else {
          this.$toast.error(response.message);
        }
      } catch (error) {
        this.showLoader = false;
        this.$toast.error("Failed to create facility services");
        console.log(error);
      }
    },
    handleServiceTypeChange(index) {
      console.log("service_type", this.services[index].service_type_id?.code);
      if (this.services[index].service_type_id?.code == "FST-OO2") {
        this.services[index].show_sample = true;
      } else {
        this.services[index].show_sample = false;
      }
    },
  },
};
</script>
